import {
  pickBestLiveValue,
  rankByTagMatch,
} from 'features/knowledgeDocumentViewer/hooks/pickBestLiveValue'
import type { LiveProcessValue } from 'features/knowledgeDocumentViewer/hooks/useLiveProcessDataValues'
import { useRolodexEntriesCachedById } from 'features/knowledgeDocumentViewer/hooks/useRolodexEntriesCachedById'
import type { MatchedDatapoint } from 'features/timeSeries/hooks/useSiteDatapoints'
import {
  type TimeSeriesDataPoint,
  useTimeSeriesService,
} from 'features/timeSeries/timeSeriesService'
import { useMemo } from 'react'
import useSWR from 'swr'
import { RolodexEntryType } from 'types'
import { z } from 'zod'

export type LiveValueTarget = {
  key: string
  label: string
  datapoints: MatchedDatapoint[]
}
const unitSchema = z.object({
  entry: z.object({
    id: z.string(),
    tags: z.object({ site: z.string() }).optional(),
    data: z.object({ unit: z.string().nullable().optional() }),
  }),
})

export function selectLiveTargets(
  targets: LiveValueTarget[]
): Map<string, { label: string; series: { id: string; piTag: string }[] }> {
  const result = new Map<
    string,
    { label: string; series: { id: string; piTag: string }[] }
  >()
  let total = 0
  for (const target of targets) {
    if (result.has(target.key)) continue
    const series = rankByTagMatch(
      target.label,
      target.datapoints.map((dp) => ({ id: dp.id, piTag: dp.pi_tag }))
    ).slice(0, Math.min(2, 400 - total))
    if (!series.length) continue
    result.set(target.key, { label: target.label, series })
    total += series.length
    if (total >= 400) break
  }
  return result
}

export function useLiveTargetValues(
  targets: LiveValueTarget[],
  scope = '',
  siteScope?: { organization: string; site: string }
): Map<string, LiveProcessValue> {
  const { fetchLatestSensorValues } = useTimeSeriesService()
  const siteOrganization = siteScope?.organization
  const siteId = siteScope?.site
  const selected = useMemo(() => selectLiveTargets(targets), [targets])
  const ids = useMemo(
    () =>
      [
        ...new Set(
          [...selected.values()].flatMap((target) =>
            target.series.map((dp) => dp.id)
          )
        ),
      ].sort(),
    [selected]
  )
  const { data: points } = useSWR(
    ids.length
      ? scope
        ? ['live-process-data-latest', scope, ids.join(',')]
        : ['live-process-data-latest', ids.join(',')]
      : null,
    async (): Promise<Record<string, TimeSeriesDataPoint | null>> => {
      const chunks: string[][] = []
      for (let i = 0; i < ids.length; i += 50) chunks.push(ids.slice(i, i + 50))
      const responses = await Promise.all(
        chunks.map((chunk) => fetchLatestSensorValues(chunk).catch(() => ({})))
      )
      const merged: Record<string, TimeSeriesDataPoint | null> = {}
      for (const response of responses) Object.assign(merged, response)
      return merged
    },
    { refreshInterval: 60000, keepPreviousData: !scope }
  )
  const unitOptions = useMemo(
    () =>
      siteOrganization && siteId
        ? {
            cacheScope: `live-target-units:${siteOrganization}:${siteId}`,
            loadOptions: {
              type: [RolodexEntryType.DATA_POINTS],
              tags: { site: siteId },
            },
          }
        : undefined,
    [siteOrganization, siteId]
  )
  const { data: entries } = useRolodexEntriesCachedById(
    ids,
    undefined,
    unitOptions
  )
  const units = useMemo(() => {
    const result = new Map<string, string>()
    for (const entry of entries ?? []) {
      const parsed = unitSchema.safeParse(entry)
      if (
        parsed.success &&
        (!siteId || parsed.data.entry.tags?.site === siteId) &&
        parsed.data.entry.data.unit
      )
        result.set(parsed.data.entry.id, parsed.data.entry.data.unit)
    }
    return result
  }, [entries, siteId])
  return useMemo(() => {
    const result = new Map<string, LiveProcessValue>()
    for (const [key, { label, series }] of selected) {
      const readings = series.flatMap(({ id, piTag }) => {
        const point = points?.[id]
        if (!point || point.value === null) return []
        const measuredAt = new Date(point.timestamp)
        return [
          {
            datapointId: id,
            piTag,
            value: point.value,
            measuredAt,
            unit: units.get(id),
            isStale: Date.now() - measuredAt.getTime() > 3600000,
          },
        ]
      })
      const best = pickBestLiveValue(label, readings)
      if (best) result.set(key, best)
    }
    return result
  }, [selected, points, units])
}
